import { createTheme } from '@mui/material';
import { ptBR } from '@mui/x-date-pickers/locales';
import { ptBR as ptBrGrid } from '@mui/material/locale';

// Definição centralizada das cores da STATO BR
const colors = {
  // Cores primárias
  blue: {
    main: '#08467c', // blue2 - cor primária principal
    light: '#118bd7', // blue3 - versão mais clara
    dark: '#151f3b', // blue1 - versão mais escura
  },
  // Cores secundárias
  green: {
    main: '#75b21d', // green3 - cor secundária principal
    light: '#8ac732', // versão mais clara
    dark: '#1c6f29', // green2 - versão mais escura
  },
  // Cores neutras
  gray: {
    main: '#585858', // cor de texto padrão
    light: '#f5f5f5', // cinza claro para backgrounds
    dark: '#333333', // cinza escuro para ênfase
  },
  // Cores de estado
  status: {
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',
  },
  // Cores de background
  background: {
    default: '#ffffff', // background principal (branco)
    paper: '#ffffff', // background de componentes elevados
    light: '#f9f9f9', // background secundário
  },
  white: '#ffffff',
};

// Definição centralizada de espaçamentos
const spacing = {
  xs: 4, // 4px
  sm: 8, // 8px
  md: 16, // 16px
  lg: 24, // 24px
  xl: 32, // 32px
  xxl: 40, // 40px
};

// Definição centralizada de bordas e elevação
const shape = {
  borderRadius: {
    small: 4, // 4px
    medium: 8, // 8px
    large: 10, // 10px
    xl: 16, // 16px
    round: '50%', // circular
  },
  boxShadow: {
    light: '0 2px 4px rgba(0,0,0,0.1)',
    medium: '0 4px 8px rgba(0,0,0,0.12)',
    strong: '0 8px 16px rgba(0,0,0,0.14)',
  },
};

// Tema centralizado
export const theme = createTheme(
  {
    // Paleta de cores
    palette: {
      primary: {
        main: colors.blue.main,
        light: colors.blue.light,
        dark: colors.blue.dark
      },
      secondary: {
        main: colors.green.main,
        light: colors.green.light,
        dark: colors.green.dark,
      },
      text: {
        primary: colors.gray.main,
        secondary: colors.gray.dark,
      },
      background: {
        default: colors.background.default,
        paper: colors.background.paper,
      },
      success: {
        main: colors.status.success,
      },
      warning: {
        main: colors.status.warning,
      },
      error: {
        main: colors.status.error,
      },
      info: {
        main: colors.status.info,
      }
    },
    // Tipografia
    typography: {
      fontFamily: 'Montserrat, Roboto, Oxygen',
      h1: {
        fontSize: '40px',
        lineHeight: '45px',
        fontWeight: '700',
        color: colors.gray.main,
      },
      h2: {
        color: colors.gray.main,
        fontSize: '35px',
        lineHeight: '40px',
        fontStyle: 'normal',
        fontWeight: 600,
      },
      h3: {
        color: colors.gray.main,
        fontSize: '30px',
        lineHeight: '33px',
        fontStyle: 'normal',
        fontWeight: 600,
      },
      h4: {
        color: colors.gray.main,
        fontSize: '27px',
        fontStyle: 'normal',
        lineHeight: '30px',
      },
      h5: {
        color: colors.gray.main,
        fontSize: '25px',
        fontWeight: 600,
        lineHeight: '27px',
      },
      subtitle1: {
        fontSize: '20px',
        fontWeight: 600,
        lineHeight: '24px',
      },
      subtitle2: {
        fontSize: '18px',
        fontWeight: 500,
        lineHeight: '20px',
      },
      body1: {
        fontSize: '16px',
        lineHeight: '24px',
      },
      body2: {
        fontSize: '14px',
        lineHeight: '20px',
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    // Customização de componentes
    components: {
      // Customização de Cards
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadius.large,
            boxShadow: shape.boxShadow.light,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: `${spacing.md}px ${spacing.md}px ${spacing.sm}px`,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: `${spacing.sm}px ${spacing.md}px`,
            '&:last-child': {
              paddingBottom: spacing.md,
            },
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: spacing.md,
            justifyContent: 'flex-end',
          },
        },
      },
      // Customização de Botões
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadius.medium,
            textTransform: 'none',
            fontWeight: 500,
            padding: `${spacing.sm}px ${spacing.lg}px`,
          },
          containedPrimary: {
            backgroundColor: colors.blue.main,
            '&:hover': {
              backgroundColor: colors.blue.dark,
            },
          },
        },
      },
      // Customização de inputs
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: `${shape.borderRadius.large}px !important`,
          },
        },
      },
      // DataGrid e outros componentes MUI-X são configurados através do arquivo de estilo global
      // ou diretamente nos componentes que os utilizam
      // Customização de Paper - padrão para cards e tabelas
      MuiPaper: {
        defaultProps: {
          elevation: 2
        },
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadius.medium,
            boxShadow: shape.boxShadow.medium,
            overflow: 'hidden'
          }
        }
      },
      // Customização de Typography
      MuiTypography: {
        styleOverrides: {
          root: {
            color: colors.gray.main,
          },
        },
      },
    },
  },
  // Localizações para datepickers e datagrids
  ptBR,
  ptBrGrid,
);

// Disponibilizar constantes do tema para uso em outros componentes
export const themeConstants = {
  colors,
  spacing,
  shape,
};

